import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Bot, TrendingUp, ShieldCheck, LineChart, Gift, Rocket } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { useAuth } from '../lib/auth';
import CheckoutModal from '../components/billing/CheckoutModal';

// Feb 2026 — FABTEXS AI Pricing page. Locked reference content: three plans,
// Standard / Pro / Enterprise. Prices are the ONLY thing that changes when
// toggling Monthly / Yearly. Feature lists and bottom benefit cards are
// exactly as specified. Colours match the master brand palette.

const STANDARD_FEATURES = [
  '1 WhatsApp Number',
  'Collections',
  'Unlimited AI Chats',
  'Persistent Chat',
  'Customer Profile',
  'Order & Despatch Management',
  'Payment verification',
  'Web Push',
  'Analytics',
  'Payment & Shipping flow',
  'Auto Catalogue Broadcasting',
  'Smart Follow-up',
  'Seller Hand-off',
  'Objection Handling',
  'Re-Engagement',
  'Cross selling',
  'Up selling',
  'Star Ratings',
  'Customised link & QR code',
];

const PRO_FEATURES = [
  'Up to 3 WhatsApp Numbers',
  'Multiple Team Members (Admin + 3 members)',
  'Buyer & Order Data Export',
  'Catalogue Access',
  'Add Instagram link',
];

const ENTERPRISE_FEATURES = [
  'Up to 10 WhatsApp Numbers',
  'Multiple Team Members (Admin + 10 members)',
  'Advance Report',
  'Branded Product Images',
  'Advance buyer segmentation',
  'Whatsapp API',
  'Schedule collection',
];

const PRICES = {
  monthly: { standard: '₹2,999/-', pro: '₹6,999/-' },
  yearly:  { standard: '₹28,790/-', pro: '₹67,190/-' },
};

const BOTTOM_BENEFITS = [
  { icon: Bot, title: 'Unlimited AI Chats', body: 'Engage buyers 24x7 with our AI sales assistant.' },
  { icon: TrendingUp, title: 'Boost Sales', body: 'Smart follow-ups, cross-sell & upsell to grow your business.' },
  { icon: ShieldCheck, title: 'Trust & Transparency', body: 'Payment verification, order tracking & real-time updates.' },
  { icon: LineChart, title: 'Insights that Matter', body: 'Analytics & reports to help you decide and scale.' },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const cur = yearly ? PRICES.yearly : PRICES.monthly;
  const { seller } = useAuth();
  const isLoggedIn = !!seller;
  const [checkout, setCheckout] = useState(null);
  const cycle = yearly ? 'yearly' : 'monthly';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f2fb] via-[#fbf7ff] to-white" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        {/* Header: mobile → stacked (brand top-left, title below); desktop → 3-column with title centered */}
        <header className="mb-3 sm:mb-4">
          <div className="hidden sm:grid grid-cols-3 items-center">
            <Link to="/home" className="flex items-center justify-self-start" data-testid="pricing-brand">
              <BrandLogo height={28} />
            </Link>
            <h1 className="font-display font-extrabold text-fab-text text-[22px] sm:text-[26px] tracking-tight justify-self-center" data-testid="pricing-title">
              Pricing plan
            </h1>
            <div />
          </div>
          <div className="sm:hidden flex flex-col gap-3">
            <Link to="/home" className="flex items-center" data-testid="pricing-brand-mobile">
              <BrandLogo height={24} />
            </Link>
            <h1 className="font-display font-extrabold text-fab-text text-[22px] tracking-tight text-center" data-testid="pricing-title-mobile">
              Pricing plan
            </h1>
          </div>
        </header>

        {/* Monthly / Yearly toggle + 20% OFF badge */}
        <div className="flex items-center justify-center flex-wrap gap-3 mb-4 sm:mb-5">
          <div className="inline-flex items-center p-1 rounded-full bg-white border border-fab-primary-lighter shadow-sm" role="tablist">
            <button
              type="button"
              onClick={() => setYearly(false)}
              className={`px-5 sm:px-6 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                !yearly ? 'bg-fab-primary text-white shadow' : 'text-fab-primary hover:bg-fab-primary-lighter'
              }`}
              data-testid="pricing-toggle-monthly"
              aria-pressed={!yearly}
            >Monthly</button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              className={`px-5 sm:px-6 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                yearly ? 'bg-fab-primary text-white shadow' : 'text-fab-primary hover:bg-fab-primary-lighter'
              }`}
              data-testid="pricing-toggle-yearly"
              aria-pressed={yearly}
            >Yearly</button>
          </div>
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200"
            data-testid="pricing-discount-badge"
          >20% OFF</span>
        </div>

        {/* Three equal-width cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 items-stretch">
          {/* STANDARD */}
          <PlanCard tier="STANDARD" testId="plan-standard">
            <PlanHeader name="Standard" priceLabel={cur.standard} gst />
            {isLoggedIn && (
              <button
                onClick={() => setCheckout({ tier: 'STANDARD', cycle })}
                className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-full bg-fab-primary text-white font-semibold text-[12.5px] shadow hover:brightness-105 transition-all"
                data-testid="pricing-subscribe-standard"
              >Subscribe to Standard</button>
            )}
            <FeatureList features={STANDARD_FEATURES} />
            <BenefitCards />
          </PlanCard>

          {/* PRO — MOST POPULAR */}
          <PlanCard tier="PRO" popular testId="plan-pro">
            <div
              className="pointer-events-none absolute top-[18px] sm:top-[26px] right-[-46px] sm:right-[-58px] rotate-45 w-[180px] sm:w-[210px] text-center py-1 text-[9.5px] sm:text-[10.5px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.15em] bg-gradient-to-r from-[#E91E63] to-[#B816F0] text-white shadow-md"
              data-testid="plan-pro-ribbon"
            >
              Most Popular
            </div>
            <PlanHeader name="Pro" priceLabel={cur.pro} gst />
            {isLoggedIn ? (
              <button
                onClick={() => setCheckout({ tier: 'PRO', cycle })}
                className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-r from-[#E91E63] to-[#B816F0] text-white font-semibold text-[12.5px] shadow hover:brightness-105 transition-all"
                data-testid="pricing-subscribe-pro"
              >Subscribe to Pro</button>
            ) : (
              <Link
                to="/signup"
                className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-r from-[#E91E63] to-[#B816F0] text-white font-semibold text-[12.5px] shadow hover:brightness-105 transition-all"
                data-testid="pricing-start-free-trial"
              >Start Free Trial</Link>
            )}
            <div className="mt-3 text-[12px] font-semibold text-fab-primary" data-testid="pro-everything-in-standard">Everything in Standard +</div>
            <FeatureList features={PRO_FEATURES} />
            <BenefitCards />
          </PlanCard>

          {/* ENTERPRISE — COMING SOON */}
          <PlanCard tier="ENTERPRISE" testId="plan-enterprise">
            <div className="text-[11px] uppercase tracking-widest text-fab-text-muted font-semibold">Enterprise</div>
            <div className="mt-1 font-display text-[22px] sm:text-[24px] font-extrabold text-fab-primary" data-testid="plan-enterprise-price">
              COMING SOON
            </div>
            <div className="mt-3 text-[12px] font-semibold text-fab-text-secondary">Everything in Pro +</div>
            <FeatureList features={ENTERPRISE_FEATURES} />
            <BenefitCards />
          </PlanCard>
        </div>

        {/* Bottom benefits row */}
        <div className="mt-4 sm:mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 lg:divide-x lg:divide-fab-primary-lighter" data-testid="pricing-bottom-benefits">
          {BOTTOM_BENEFITS.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className={`flex flex-col items-center text-center px-3 ${i === 0 ? '' : 'lg:pl-4'}`}>
              <div className="w-8 h-8 rounded-xl bg-fab-primary-lighter text-fab-primary flex items-center justify-center mb-1.5">
                <Icon className="w-4 h-4" />
              </div>
              <div className="font-display font-bold text-fab-text text-[13px]">{title}</div>
              <div className="text-[11.5px] text-fab-text-secondary mt-0.5 leading-snug max-w-[220px]">
                {body}
              </div>
            </div>
          ))}
        </div>
      </div>

      <CheckoutModal
        open={!!checkout}
        onClose={() => setCheckout(null)}
        defaultTier={checkout?.tier || 'PRO'}
        defaultCycle={checkout?.cycle || 'monthly'}
      />
    </div>
  );
}

// ── Small building blocks ───────────────────────────────────────────────

function PlanCard({ children, popular, testId }) {
  // Feb 2026 UX polish — every card gets a pink border. Pro keeps a stronger
  // pink glow so it still reads as the highlighted "Most Popular" plan while
  // Standard and Enterprise share the same pink accent.
  return (
    <div
      className={`relative bg-white rounded-2xl border-2 p-4 sm:p-5 flex flex-col overflow-hidden ${
        popular
          ? 'border-[#E91E63] shadow-[0_18px_50px_-20px_rgba(233,30,99,0.45)] lg:scale-[1.02]'
          : 'border-[#F472B6]/70 shadow-sm'
      }`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function PlanHeader({ name, priceLabel, gst }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-fab-text-muted font-semibold">{name}</div>
      <div className="mt-1 flex items-baseline gap-1.5" data-testid={`plan-${name.toLowerCase()}-price`}>
        <span className="font-display text-[22px] sm:text-[24px] font-extrabold text-fab-primary">{priceLabel}</span>
        {gst && <span className="text-[11.5px] text-fab-text-muted">+ GST</span>}
      </div>
    </div>
  );
}

function FeatureList({ features }) {
  return (
    <ul className="mt-2.5 space-y-1 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-[12px] text-fab-text leading-snug">
          <span className="w-3.5 h-3.5 mt-0.5 shrink-0 rounded-full bg-fab-primary-lighter text-fab-primary flex items-center justify-center">
            <Check className="w-2 h-2" strokeWidth={3} />
          </span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

function BenefitCards() {
  return (
    <div className="mt-3 grid grid-cols-1 gap-1.5">
      <div className="flex items-center gap-2 rounded-lg border border-fab-primary-lighter bg-fab-primary-lighter/40 px-2.5 py-1.5">
        <Gift className="w-3.5 h-3.5 text-fab-primary shrink-0" />
        <div className="min-w-0 leading-tight">
          <div className="text-[11.5px] font-bold text-fab-text">VyaparBiz FREE</div>
          <div className="text-[10px] text-fab-text-muted">(Unlock after subscription)</div>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-fab-primary-lighter bg-fab-primary-lighter/40 px-2.5 py-1.5">
        <Rocket className="w-3.5 h-3.5 text-fab-primary shrink-0" />
        <div className="min-w-0 leading-tight">
          <div className="text-[11.5px] font-bold text-fab-text">FABTEXS PLATFORM ONBOARDING</div>
          <div className="text-[10px] text-fab-text-muted">(Unlock after subscription)</div>
        </div>
      </div>
    </div>
  );
}
